import {observer} from "mobx-react";
import {AudioExampleModel, AudioFileModel} from "../../../shared/models/AudioTestModel";
import React, {useEffect, useState} from "react";
import Grid from "@material-ui/core/Grid";
import {SurveyControlRender} from "../../forms/SurveyControl.render";
import {AudioButton, AudioController, useAudioPlayer} from "../../web-audio/AudiosPlayer";
import {AudioLoading, useAllAudioRefsReady} from "../../web-audio/AudiosLoading";
import {useRandomization} from "../../../shared/tools/RandomizationTools";
import {ratingAreaStyle} from "../../../shared/SharedStyles";
import {AudioSectionLoopingController} from "../../web-audio/AudioSectionLoopingController";
import {Box, Slider} from "@material-ui/core";

export const MushraTestItemExampleRender = observer(function (props: { example: AudioExampleModel, active?: boolean, onAudioLoadingChange?: (loading: boolean) => void }) {
  const {example, active, onAudioLoadingChange} = props;
  // Randomize first to make sure random audio match the dom tree
  const [randomAudios] = useRandomization(example.medias, active && example.settings?.randomMedia, example.settings?.fixLastInternalQuestion);
  // This is a custom hook that expose some functions for AudioButton and Controller
  const {refs, sampleRef, activeRef, loopRange, setLoopRange, currentTime, handleTimeUpdate, handlePlay, handlePause, handleEnded, handleSeek} = useAudioPlayer(randomAudios, example.mediaRef, example);
  const allRefs = example.mediaRef ? [...refs, sampleRef] : refs;
  const allAudios = example.mediaRef ? [...randomAudios, example.mediaRef] : randomAudios;
  const {loading, loadError, progress, sources, retry} = useAllAudioRefsReady(allRefs, allAudios, active !== false);
  // An event for setting Time update method
  const [onTimeUpdate, setOnTimeUpdate] = useState<() => void>();

  useEffect(() => {
    if (active === false) handlePause();
  }, [active]);

  return <> <AudioLoading showing={loading} error={loadError} progress={progress} onRetry={retry} onLoadingChange={onAudioLoadingChange}/>
    <Grid container spacing={2} style={{display: loading || loadError ? 'none' : 'flex'}}>
      {example.fields?.map((value, i) => <Grid item xs={12} key={i}>
        <SurveyControlRender control={value}/>
      </Grid>)}

      {randomAudios.map((v, i) => <Grid item key={i} style={ratingAreaStyle}>
        <MusharaRatingBar audio={v}/>
        <AudioButton ref={refs[i]} audio={v} source={sources[v.src]} load={active !== false} onPlay={handlePlay} onPause={handlePause}
                     onEnded={handleEnded}
                     onTimeUpdate={() => {
                       handleTimeUpdate();
                       onTimeUpdate?.();
                     }}>{i + 1}</AudioButton>
        {/*{isDevMode() && <span>{refs[i].current?.currentTime}</span>}*/}
      </Grid>)}

      {/*Reference*/}
      {example.mediaRef && <Grid item style={ratingAreaStyle}>
        <AudioButton ref={sampleRef} audio={example.mediaRef} source={sources[example.mediaRef.src]} load={active !== false} onPlay={handlePlay} onPause={handlePause}
                     onEnded={handleEnded} onTimeUpdate={() => {
          handleTimeUpdate();
          onTimeUpdate?.();
        }}>Ref</AudioButton>
        {/*{isDevMode() && <span>{sampleRef?.current?.currentTime}</span>}*/}
      </Grid>}

      <Grid item xs={12}>
        <AudioController activeRef={activeRef} currentTime={currentTime} onSeek={handleSeek}
                         disabled={example.settings?.disablePlayerSlider}/>
        {example.settings?.sectionLooping &&
        <AudioSectionLoopingController setTimeUpdate={f => setOnTimeUpdate(f)} audioRef={activeRef}
                                       currentTime={currentTime} range={loopRange} onRangeChange={setLoopRange}/>}
      </Grid>
    </Grid>
  </>
});

const marks = [
  {value: 0, label: '0'},
  {value: 20, label: '20'},
  {value: 40, label: '40'},
  {value: 60, label: '60'},
  {value: 80, label: '80'},
  {value: 100, label: '100'},
];

export const MusharaRatingBar = observer(function (props: { audio: AudioFileModel }) {
  const rating = Number(props.audio.value);
  const ratingValue = Number.isFinite(rating) ? rating : 0;

  useEffect(() => {
    // Scores can be absent when a participant first opens a test.  Keep the
    // model and the controlled slider in sync without rendering NaN first.
    if (!Number.isFinite(rating)) props.audio.value = '0';
  }, [props.audio, rating]);

  return <Box ml={2.5} mb={2} mt={2} style={{height: 200}}>
    <Slider orientation="vertical" aria-labelledby="vertical-slider" min={0} max={100} step={1} marks={marks}
            valueLabelDisplay="auto" value={ratingValue}
            onChange={(_, value) => props.audio.value = value.toString()}/>
  </Box>
})
